import type { TFunction } from "i18next";
import { PortalAwardOutcomeDto, type PortalAwardLetterDto } from "../../../../autogen/bff-api";
import { winnerNamesText } from "../../../v2/sourcing-event/award/awardLetterTemplates";

export interface AwardOutcomeMessage {
  title: string;
  body: string | null;
  ranking: string | null;
}

const ownOutcome = (outcome: PortalAwardOutcomeDto, t: TFunction): string =>
  outcome === PortalAwardOutcomeDto.Rejected ? t("Your bid was rejected.") : t("Your bid was not selected.");

const winnerBody = (eventRunsPrivateFlow: boolean, t: TFunction): string =>
  eventRunsPrivateFlow
    ? t("You will be notified when the buyer opens for signing the contract.")
    : t("The contract can be signed once the standstill period is over. You will be notified when the buyer opens for signing.");

const rankingText = (award: PortalAwardLetterDto, t: TFunction): string | null =>
  award.outcome === PortalAwardOutcomeDto.NotSelected && award.myRank != null && award.myRank > 1 && award.bidderCount != null
    ? t("You were ranked number {{rank}} of {{count}} evaluated bids.", { rank: award.myRank, count: award.bidderCount })
    : null;

export const awardOutcomeMessage = (
  award: PortalAwardLetterDto,
  eventRunsPrivateFlow: boolean,
  t: TFunction,
): AwardOutcomeMessage | null => {
  const outcome = award.outcome;
  if (!outcome) return null;

  if (outcome === PortalAwardOutcomeDto.Withdrawn) {
    return {
      title: t("The award has been withdrawn"),
      body: t(
        "The buyer has reversed the award decision. The competition is back in evaluation, and you will be notified when there is a new decision.",
      ),
      ranking: null,
    };
  }

  if (outcome === PortalAwardOutcomeDto.Winner) {
    return { title: t("You won the competition."), body: winnerBody(eventRunsPrivateFlow, t), ranking: null };
  }

  const winner = winnerNamesText(award.winnerNames ?? []);
  return {
    title: winner ? t("The contract was awarded to {{winner}}.", { winner }) : ownOutcome(outcome, t),
    body: winner ? ownOutcome(outcome, t) : null,
    ranking: rankingText(award, t),
  };
};
